import { useQuery } from '@tanstack/react-query';
import { useBoardRepository, useReportRepository } from '../container';
import { boardRefetchInterval } from '../live';
import { keys } from '../query-keys';
import type { StaffPeriod } from '../ports/ReportRepository';

/**
 * The live floor board. Section 8.2 sets its cache policy explicitly: five
 * seconds, against thirty for every other list. It is the one screen somebody
 * leaves open on a wall, and a stage count that is half a minute stale is worse
 * than no board at all.
 */
export function useBoard(enabled = true) {
  const repo = useBoardRepository();
  return useQuery({
    queryKey: keys.board(),
    queryFn: () => repo.board(),
    // Off for a caller the server would refuse: the board backs the production
    // line and the overview, and the assign dialog asks it who is timing a tray.
    enabled,
    staleTime: 5_000,
    // Every five seconds without the stream, once a minute with it. Section 24
    // of the review of 2 October 2026.
    refetchInterval: boardRefetchInterval,
  });
}

/**
 * Per technician productivity. Refused to `tech` either way.
 *
 * `mine` asks for the caller's own row only, which is the My performance
 * screen. The whole floor needs the `staff` view, one's own needs `perf`, and
 * those are granted to different roles.
 */
export function useStaffReport(period: StaffPeriod, mine = false, enabled = true) {
  const repo = useReportRepository();
  const key = typeof period === 'string' ? period : `${period.from}..${period.to}`;
  return useQuery({
    queryKey: keys.reports('staff', key, { mine }),
    queryFn: () => repo.staff(period, mine),
    // Not asked on behalf of a reader the server would refuse.
    enabled,
    staleTime: 30_000,
  });
}
